import React, { useMemo, useState } from 'react';
import KoobButton from '@koob/ui/src/components/KoobButton';
import KoobModal from '@koob/ui/src/components/KoobModal';
import KoobTitle from '@koob/ui/src/components/KoobTitle';
import TripTemplateImageSelector from './TripTemplateImageSelector';
import TripTemplateImageUpload from './TripTemplateImageUpload';
import useTripStore from '../../store/useTripStore';
import { useDays } from '../../hooks/useTripStoreSelectors';
import { getTemplateImages } from '../../logic/getTemplateImages';

interface TripTemplateValues {
  name: string;
  imageUrl: string;
}

interface TripTemplateModalProps {
  open: boolean;
  onClose: () => void;
  // Saves the template. The modal waits for it, then closes; if it rejects the
  // modal stays open with what the user typed.
  onSave: (values: TripTemplateValues) => Promise<unknown>;
  translation: (key: string, options?: Record<string, any>) => string;
  importImageLabel: string;
  title?: React.ReactNode;
  saveLabel?: React.ReactNode;
  namePlaceholder?: string;
  initialName?: string;
  initialImageUrl?: string | null;
  // The pictures to pick from; the trip's own ones when left out.
  images?: string[];
  // Fields and actions the app adds around the shared ones.
  extraFields?: React.ReactNode;
  extraActions?: React.ReactNode;
  // Lets the app hold the save back for its own fields.
  canSave?: boolean;
}

/**
 * Names a template and picks its picture: one of the trip's, or one the user
 * loads. What saving does — which call, which side effects — is the app's.
 */
export default function TripTemplateModal({
  open,
  onClose,
  onSave,
  translation: t,
  importImageLabel,
  title = t('compose.saveAsTemplate'),
  saveLabel = t('compose.saveAsTemplate'),
  namePlaceholder = t('templateModal.namePlaceholder'),
  initialName = '',
  initialImageUrl = null,
  images,
  extraFields,
  extraActions,
  canSave = true,
}: TripTemplateModalProps) {
  const days = useDays();
  const experiences = useTripStore((state) => state.experiences);
  const [name, setName] = useState(initialName);
  const [saving, setSaving] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(
    initialImageUrl,
  );
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);

  const suggestedImages = useMemo(
    () => images ?? getTemplateImages(days, experiences),
    [images, days, experiences],
  );

  const handleSave = async () => {
    setSaving(true);

    try {
      await onSave({ name, imageUrl: selectedImage as string });
      // A new template starts blank again; an edited one keeps what was just
      // saved.
      if (!initialName) {
        setName('');
      }
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <KoobModal open={open} setOpen={() => onClose()}>
      <KoobTitle className="text-center">{title}</KoobTitle>

      <p className="font-bold text-gray-500">{t('templateModal.name')}</p>
      <input
        type="text"
        value={name}
        onChange={(event) => setName(event.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
        placeholder={namePlaceholder}
        data-testid="input-template-name"
      />

      {extraFields}

      <div className="mt-3">
        <p className="font-bold text-gray-500">{t('templateModal.preview')}</p>

        <div className="flex gap-4">
          <TripTemplateImageSelector
            images={suggestedImages}
            selectedImage={selectedImage}
            onSelect={(image) => {
              setSelectedImage(image);
              setUploadedImage(null);
            }}
          />

          <TripTemplateImageUpload
            uploadedImage={uploadedImage}
            selectedImage={selectedImage}
            importLabel={importImageLabel}
            onUpload={(image) => {
              setUploadedImage(image);
              if (image) {
                setSelectedImage(image);
              }
            }}
          />
        </div>
      </div>

      {extraActions}

      <div className="mt-5">
        <KoobButton
          variant="primary"
          block
          onClick={handleSave}
          loading={saving}
          disabled={!name || !selectedImage || !canSave}
          testId="btn-finalize-template"
        >
          {saveLabel}
        </KoobButton>
      </div>
    </KoobModal>
  );
}
